<template>
  <div class="faq-list">
    <div
      v-for="(item, index) in items"
      :key="index"
      class="faq-item"
      :class="{ open: openIndex === index }"
    >
      <button
        class="faq-trigger"
        :aria-expanded="openIndex === index ? 'true' : 'false'"
        @click="toggle(index)"
      >
        <span class="faq-q">{{ item.question }}</span>
        <span class="faq-icon" aria-hidden="true"></span>
      </button>
      <div class="faq-body" role="region">
        <div class="faq-body-inner" v-html="item.answer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { notifyLayoutAnimation } from "~/core/utils/layoutAnimation";

const FAQ_ANIM_MS = 300;

const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export default {
  name: "FaqAccordion",
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      openIndex: -1,
      rafIds: {},
    };
  },
  beforeUnmount() {
    Object.values(this.rafIds).forEach((id) => cancelAnimationFrame(id));
  },
  methods: {
    body(index) {
      return this.$el ? this.$el.querySelectorAll(".faq-body")[index] : null;
    },
    // Whole-pixel JS height animation. A CSS max-height transition passes
    // through FRACTIONAL heights every frame and iOS WebKit re-rounds the
    // surrounding paint each frame — the page visibly wobbles 1–3px while
    // the panel animates. Rounded steps keep every intermediate layout on
    // the pixel grid. (The hide-on-scroll header is frozen for the duration
    // via notifyLayoutAnimation, see toggle().)
    animateBody(index, from, to) {
      const body = this.body(index);
      if (!body) return;

      if (this.rafIds[index]) cancelAnimationFrame(this.rafIds[index]);

      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      const finish = () => {
        delete this.rafIds[index];
        // Open panels settle to natural height, closed ones back to CSS 0.
        body.style.height = to > 0 ? "auto" : "";
      };

      if (reduceMotion) {
        finish();
        return;
      }

      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / FAQ_ANIM_MS);
        body.style.height = `${Math.round(
          from + (to - from) * easeInOutCubic(t)
        )}px`;
        if (t >= 1) {
          finish();
          return;
        }
        this.rafIds[index] = requestAnimationFrame(tick);
      };
      this.rafIds[index] = requestAnimationFrame(tick);
    },
    currentHeight(index) {
      const body = this.body(index);
      return body ? Math.round(body.getBoundingClientRect().height) : 0;
    },
    contentHeight(index) {
      const body = this.body(index);
      const inner = body && body.firstElementChild;
      return inner ? Math.round(inner.getBoundingClientRect().height) : 0;
    },
    toggle(index) {
      // Freeze the hide-on-scroll header while the document height animates —
      // otherwise iOS Safari's scroll adjustments make the header react and
      // the whole page shakes.
      notifyLayoutAnimation(FAQ_ANIM_MS);

      const previous = this.openIndex;

      if (previous === index) {
        this.openIndex = -1;
        this.animateBody(index, this.currentHeight(index), 0);
        return;
      }

      if (previous !== -1) {
        this.animateBody(previous, this.currentHeight(previous), 0);
      }

      this.openIndex = index;
      this.animateBody(
        index,
        this.currentHeight(index),
        this.contentHeight(index)
      );
    },
  },
};
</script>
